import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Users, 
  Save, 
  RefreshCw, 
  Plus, 
  Edit, 
  Trash2,
  CheckCircle, 
  AlertCircle,
  User,
  Clock,
  TrendingUp,
  Award,
  Target,
  Calendar,
  Eye,
  Filter,
  Search,
  Building2,
  Shield
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import AddEditAgentModal from '../../components/modals/AddEditAgentModal';
import ViewAgentModal from '../../components/modals/ViewAgentModal';
import AgentPerformanceModal from '../../components/modals/AgentPerformanceModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import AgentStatusFilterModal from '../../components/modals/AgentStatusFilterModal';
import DepartmentFilterModal from '../../components/modals/DepartmentFilterModal';
import RoleFilterModal from '../../components/modals/RoleFilterModal';
import TimezoneFilterModal from '../../components/modals/TimezoneFilterModal';
import AgentsFilterModal from '../../components/modals/AgentsFilterModal';

const AgentsManagement = () => {
  const [activeTab, setActiveTab] = useState('agents');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]); // Store all agents before filtering
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [assignmentRules, setAssignmentRules] = useState({});
  const [availabilitySettings, setAvailabilitySettings] = useState({});
  const [message, setMessage] = useState(null);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTimezones, setSelectedTimezones] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showViewAgentModal, setShowViewAgentModal] = useState(false);
  const [viewingAgent, setViewingAgent] = useState(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [performanceAgent, setPerformanceAgent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  
  // Filter modal states
  const [showStatusFilterModal, setShowStatusFilterModal] = useState(false);
  const [showDepartmentFilterModal, setShowDepartmentFilterModal] = useState(false);
  const [showRoleFilterModal, setShowRoleFilterModal] = useState(false);
  const [showTimezoneFilterModal, setShowTimezoneFilterModal] = useState(false);
  const [showAllFiltersModal, setShowAllFiltersModal] = useState(false);
  
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    loadAgentData();
  }, []);

  const loadAgentData = async () => {
    setLoading(true);
    try {
      const [agentsRes, performanceRes, rulesRes, availabilityRes, departmentsRes, rolesRes] = await Promise.all([
        MockDataService.getAgents(),
        MockDataService.getAgentPerformanceMetrics(),
        MockDataService.getAssignmentRules(),
        MockDataService.getAvailabilitySettings(),
        MockDataService.getDepartments(),
        MockDataService.getRoles()
      ]);
      
      setAllAgents(agentsRes.data || []);
      setAgents(agentsRes.data || []);
      setPerformanceMetrics(performanceRes.data);
      setAssignmentRules(rulesRes.data);
      setAvailabilitySettings(availabilityRes.data);
      setDepartments(departmentsRes.data || []);
      setRoles(rolesRes.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load agent data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving agent settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.setItem('assignmentRules', JSON.stringify(assignmentRules));
      localStorage.setItem('availabilitySettings', JSON.stringify(availabilitySettings));
      
      setMessage({ type: 'success', text: 'Agent settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save agent settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAgent = async (agentData) => {
    setSaving(true);
    try {
      if (editingAgent) {
        // Update existing agent
        const response = await MockDataService.updateUser(editingAgent.id, agentData);
        if (response.success) {
          setMessage({ type: 'success', text: 'Agent updated successfully' });
          await loadAgentData();
        }
      } else {
        // Create new agent
        const response = await MockDataService.createUser(agentData);
        if (response.success) {
          setMessage({ type: 'success', text: 'Agent created successfully' });
          await loadAgentData();
        }
      }
      setShowAgentForm(false);
      setEditingAgent(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save agent' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAgent = () => {
    setShowAgentForm(true);
    setEditingAgent(null);
  };

  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setShowAgentForm(true);
  };

  const handleViewAgent = (agent) => {
    setViewingAgent(agent);
    setShowViewAgentModal(true);
  };

  const handleDeleteAgent = (agentId) => {
    setAgentToDelete(agentId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;
    
    try {
      // In a real app, this would call an API: await deleteAgent(agentToDelete);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessage({ type: 'success', text: 'Agent deleted successfully' });
      await loadAgentData();
      setShowDeleteConfirm(false);
      setAgentToDelete(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete agent' });
    }
  };


  const handleViewPerformance = (agent) => {
    setPerformanceAgent(agent);
    setShowPerformanceModal(true);
  };

  const handleApplyAllFilters = (filters) => {
    setSelectedStatuses(filters.statuses || []);
    setSelectedDepartments(filters.departments || []);
    setSelectedRoles(filters.roles || []);
    setSelectedTimezones(filters.timezones || []);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'busy': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'away': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Filter agents based on selected filters and search query
  useEffect(() => {
    let filteredAgents = [...allAgents];

    // Filter by status (single selection from dropdown)
    if (selectedStatus) {
      filteredAgents = filteredAgents.filter(agent => 
        agent.status?.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Filter by multiple statuses (from popup)
    if (selectedStatuses.length > 0) {
      filteredAgents = filteredAgents.filter(agent =>
        selectedStatuses.includes(agent.status?.toLowerCase())
      );
    }

    // Filter by departments
    if (selectedDepartments.length > 0) {
      filteredAgents = filteredAgents.filter(agent =>
        selectedDepartments.includes(agent.department) ||
        selectedDepartments.some(dept => 
          dept === agent.department || 
          departments.find(d => (d.id === dept || d.name === dept) && d.name === agent.department)
        )
      );
    }

    // Filter by roles
    if (selectedRoles.length > 0) {
      filteredAgents = filteredAgents.filter(agent =>
        selectedRoles.includes(agent.role) ||
        selectedRoles.some(role =>
          role === agent.role ||
          roles.find(r => (r.id === role || r.name === role) && r.name === agent.role)
        )
      );
    }

    // Filter by timezones
    if (selectedTimezones.length > 0) {
      filteredAgents = filteredAgents.filter(agent =>
        selectedTimezones.includes(agent.timezone) ||
        selectedTimezones.includes(agent.availability?.timezone)
      );
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filteredAgents = filteredAgents.filter(agent =>
        agent.name?.toLowerCase().includes(query) ||
        agent.email?.toLowerCase().includes(query) ||
        agent.department?.toLowerCase().includes(query)
      );
    }

    setAgents(filteredAgents);
  }, [selectedStatus, selectedStatuses, selectedDepartments, selectedRoles, selectedTimezones, searchQuery, allAgents, departments, roles]);

  const agentStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'busy', label: 'Busy' },
    { value: 'away', label: 'Away' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Agents Management</h1>
          <p className="text-gray-600 mt-2">Manage agent accounts and performance</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadAgentData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleCreateAgent}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Agent
          </Button>
        </div>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('agents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'agents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Agents List
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'performance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TrendingUp className="h-4 w-4 inline mr-2" />
              Performance
            </button>
            <button
              onClick={() => setActiveTab('assignment')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'assignment'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Target className="h-4 w-4 inline mr-2" />
              Assignment Rules
            </button>
            <button
              onClick={() => setActiveTab('availability')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'availability'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="h-4 w-4 inline mr-2" />
              Availability
            </button>
          </nav>
        </div>

        {activeTab === 'agents' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Agents List
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search agents..."
                        className="pl-10 w-64"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    
                    {/* Main Filter Button - Opens All Filters Modal */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAllFiltersModal(true)}
                      className={`flex items-center gap-2 ${
                        (selectedStatuses.length > 0 || selectedDepartments.length > 0 || 
                         selectedRoles.length > 0 || selectedTimezones.length > 0) 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : ''
                      }`}
                    >
                      <Filter className="h-4 w-4" />
                      Filters
                      {(selectedStatuses.length > 0 || selectedDepartments.length > 0 || 
                        selectedRoles.length > 0 || selectedTimezones.length > 0) && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                          {selectedStatuses.length + selectedDepartments.length + 
                           selectedRoles.length + selectedTimezones.length}
                        </span>
                      )}
                    </Button>
                    
                    {/* Individual Filter Buttons - Optional Quick Access */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowStatusFilterModal(true)}
                      className={`flex items-center gap-2 ${selectedStatuses.length > 0 ? 'text-blue-700' : ''}`}
                    >
                      <Filter className="h-4 w-4" />
                      Status
                      {selectedStatuses.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                          {selectedStatuses.length}
                        </span>
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDepartmentFilterModal(true)}
                      className={`flex items-center gap-2 ${selectedDepartments.length > 0 ? 'text-blue-700' : ''}`}
                    >
                      <Building2 className="h-4 w-4" />
                      Department
                      {selectedDepartments.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                          {selectedDepartments.length}
                        </span>
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowRoleFilterModal(true)}
                      className={`flex items-center gap-2 ${selectedRoles.length > 0 ? 'text-blue-700' : ''}`}
                    >
                      <Shield className="h-4 w-4" />
                      Role
                      {selectedRoles.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                          {selectedRoles.length}
                        </span>
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTimezoneFilterModal(true)}
                      className={`flex items-center gap-2 ${selectedTimezones.length > 0 ? 'text-blue-700' : ''}`}
                    >
                      <Clock className="h-4 w-4" />
                      Timezone
                      {selectedTimezones.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                          {selectedTimezones.length}
                        </span>
                      )}
                    </Button>

                    {/* Clear All Filters */}
                    {(selectedStatuses.length > 0 || selectedDepartments.length > 0 || selectedRoles.length > 0 || selectedTimezones.length > 0 || selectedStatus) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedStatus(null);
                          setSelectedStatuses([]);
                          setSelectedDepartments([]);
                          setSelectedRoles([]);
                          setSelectedTimezones([]);
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>

                {agents.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">No agents found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedStatus || searchQuery.trim() !== ''
                        ? 'Try adjusting your filters'
                        : 'No agents available'}
                    </p>
                    {(selectedStatus || searchQuery.trim() !== '') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedStatus(null);
                          setSearchQuery('');
                        }}
                        className="mt-4"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  {agents.map((agent) => (
                    <div key={agent.id} className="flex items-center justify-between p-5 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all bg-white">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-sm">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-base">{agent.name}</h3>
                            <Badge variant="outline" className={`${getStatusColor(agent.status)} text-xs font-medium`}>
                              {agent.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2 truncate">{agent.email}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3.5 w-3.5" />
                              {agent.department}
                            </span>
                            {agent.role && (
                              <span className="flex items-center gap-1">
                                <Shield className="h-3.5 w-3.5" />
                                {agent.role}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6 ml-4">
                        <div className="text-right border-r border-gray-200 pr-6">
                          <div className="text-sm font-semibold text-gray-900 mb-0.5">
                            {agent.ticketsResolved || 0}
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            Tickets Resolved
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            Avg: {agent.avgResponseTime || '2.5h'}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewAgent(agent)}
                            title="View Details"
                            className="h-9 px-3 border-blue-200 bg-white text-blue-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all shadow-sm"
                          >
                            <Eye className="h-4 w-4 mr-1.5" />
                            <span className="text-xs font-medium">View</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAgent(agent)}
                            title="Edit Agent"
                            className="h-9 px-3 border-amber-200 bg-white text-amber-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all shadow-sm"
                          >
                            <Edit className="h-4 w-4 mr-1.5" />
                            <span className="text-xs font-medium">Edit</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteAgent(agent.id)}
                            title="Delete Agent"
                            className="h-9 px-3 border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all shadow-sm"
                          >
                            <Trash2 className="h-4 w-4 mr-1.5" />
                            <span className="text-xs font-medium">Delete</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Agent Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Total Agents</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-900">
                      {performanceMetrics.totalAgents || 12}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900">Active Agents</span>
                    </div>
                    <div className="text-2xl font-bold text-green-900">
                      {performanceMetrics.activeAgents || 10}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-900">Avg Response Time</span>
                    </div>
                    <div className="text-2xl font-bold text-yellow-900">
                      {performanceMetrics.avgResponseTime || '2.3h'}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Satisfaction Score</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-900">
                      {performanceMetrics.satisfactionScore || '4.7/5'}
                    </div>
                  </div>
                </div>

                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold">Top Performing Agents</h3>
                      {agents.slice(0, 5).map((agent, index) => (
                        <div 
                          key={agent.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleViewPerformance(agent)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{agent.name}</h4>
                              <p className="text-sm text-gray-600">{agent.department}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className={`text-lg font-bold ${getPerformanceColor(agent.performanceScore || 85)}`}>
                                {agent.performanceScore || 85}%
                              </div>
                              <div className="text-xs text-gray-500">Performance Score</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {agent.ticketsResolved || 0}
                              </div>
                              <div className="text-xs text-gray-500">Tickets Resolved</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPerformance(agent);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'assignment' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Assignment Rules Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableAutoAssignment">Enable Auto Assignment</Label>
                      <p className="text-sm text-gray-600">Automatically assign tickets to agents</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableAutoAssignment"
                      checked={assignmentRules.enableAutoAssignment || false}
                      onChange={(e) => setAssignmentRules({...assignmentRules, enableAutoAssignment: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="assignmentStrategy" className="text-sm font-medium text-gray-700">
                        Assignment Strategy
                      </Label>
                      <div className="mt-1">
                        <StatusFilterDropdown
                          value={assignmentRules.strategy || null}
                          options={[
                            { value: 'round-robin', label: 'Round Robin' },
                            { value: 'least-busy', label: 'Least Busy' },
                            { value: 'skill-based', label: 'Skill Based' },
                            { value: 'workload-balanced', label: 'Workload Balanced' },
                          ]}
                          placeholder="Select Strategy"
                          onChange={(value) => setAssignmentRules({...assignmentRules, strategy: value})}
                          icon={Target}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxTicketsPerAgent">Max Tickets per Agent</Label>
                      <Input
                        id="maxTicketsPerAgent"
                        type="number"
                        value={assignmentRules.maxTicketsPerAgent || 10}
                        onChange={(e) => setAssignmentRules({...assignmentRules, maxTicketsPerAgent: parseInt(e.target.value)})}
                        min="1"
                        max="50"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="skillMatching">Enable Skill Matching</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="skillMatching"
                        checked={assignmentRules.skillMatching || false}
                        onChange={(e) => setAssignmentRules({...assignmentRules, skillMatching: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Match tickets to agents based on skills</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="workloadBalancing">Enable Workload Balancing</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="workloadBalancing"
                        checked={assignmentRules.workloadBalancing || false}
                        onChange={(e) => setAssignmentRules({...assignmentRules, workloadBalancing: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Distribute tickets evenly across agents</span>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Assignment Rules</h3>
                      <p className="text-sm text-gray-600">Configure how tickets are assigned to agents</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Rules'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'availability' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Agent Availability Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableAvailabilityTracking">Enable Availability Tracking</Label>
                      <p className="text-sm text-gray-600">Track agent availability status</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableAvailabilityTracking"
                      checked={availabilitySettings.enableTracking || false}
                      onChange={(e) => setAvailabilitySettings({...availabilitySettings, enableTracking: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="defaultStatus" className="text-sm font-medium text-gray-700">
                        Default Status
                      </Label>
                      <div className="mt-1">
                        <StatusFilterDropdown
                          value={availabilitySettings.defaultStatus || null}
                          options={[
                            { value: 'active', label: 'Active' },
                            { value: 'away', label: 'Away' },
                            { value: 'busy', label: 'Busy' },
                            { value: 'inactive', label: 'Inactive' },
                          ]}
                          placeholder="Select Default Status"
                          onChange={(value) => setAvailabilitySettings({...availabilitySettings, defaultStatus: value})}
                          icon={Calendar}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="autoAwayTimeout">Auto Away Timeout (minutes)</Label>
                      <Input
                        id="autoAwayTimeout"
                        type="number"
                        value={availabilitySettings.autoAwayTimeout || 30}
                        onChange={(e) => setAvailabilitySettings({...availabilitySettings, autoAwayTimeout: parseInt(e.target.value)})}
                        min="5"
                        max="120"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="workingHours">Working Hours</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="startTime">Start Time</Label>
                        <Input
                          id="startTime"
                          type="time"
                          value={availabilitySettings.startTime || '09:00'}
                          onChange={(e) => setAvailabilitySettings({...availabilitySettings, startTime: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="endTime">End Time</Label>
                        <Input
                          id="endTime"
                          type="time"
                          value={availabilitySettings.endTime || '17:00'}
                          onChange={(e) => setAvailabilitySettings({...availabilitySettings, endTime: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="workingDays">Working Days</Label>
                    <div className="grid grid-cols-7 gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                        <div key={day} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={day.toLowerCase()}
                            checked={availabilitySettings.workingDays?.[day.toLowerCase()] || (day !== 'Sat' && day !== 'Sun')}
                            onChange={(e) => setAvailabilitySettings({
                              ...availabilitySettings,
                              workingDays: {
                                ...availabilitySettings.workingDays,
                                [day.toLowerCase()]: e.target.checked
                              }
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Label htmlFor={day.toLowerCase()} className="text-sm">{day}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Availability Settings</h3>
                      <p className="text-sm text-gray-600">Configure agent availability and working hours</p>
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
            )}
          </div>

          {/* Add/Edit Agent Modal */}
          <AddEditAgentModal
            isOpen={showAgentForm}
            onClose={() => {
              setShowAgentForm(false);
              setEditingAgent(null);
            }}
            agent={editingAgent}
            onSave={handleSaveAgent}
          />

          {/* View Agent Modal */}
          <ViewAgentModal
            isOpen={showViewAgentModal}
            onClose={() => {
              setShowViewAgentModal(false);
              setViewingAgent(null);
            }}
            agent={viewingAgent}
            onEdit={handleEditAgent}
          />

          {/* Agent Performance Modal */}
          <AgentPerformanceModal
            isOpen={showPerformanceModal}
            onClose={() => {
              setShowPerformanceModal(false);
              setPerformanceAgent(null);
            }}
            agent={performanceAgent}
            metrics={performanceMetrics}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => {
              setShowDeleteConfirm(false);
              setAgentToDelete(null);
            }}
            onConfirm={confirmDeleteAgent}
            title="Delete Agent"
            message={`Are you sure you want to delete this agent? This action cannot be undone.`}
            confirmText="Delete"
            cancelText="Cancel"
            type="danger"
          />

          {/* Filter Modals */}
          <AgentStatusFilterModal
            isOpen={showStatusFilterModal}
            onClose={() => setShowStatusFilterModal(false)}
            onApply={setSelectedStatuses}
            selectedStatuses={selectedStatuses}
            statusOptions={agentStatusOptions.map(opt => ({
              value: opt.value,
              label: opt.label,
              color: getStatusColor(opt.value).split(' ').join(' ')
            }))}
          />

          <DepartmentFilterModal
            isOpen={showDepartmentFilterModal}
            onClose={() => setShowDepartmentFilterModal(false)}
            onApply={setSelectedDepartments}
            selectedDepartments={selectedDepartments}
            departments={departments}
          />

          <RoleFilterModal
            isOpen={showRoleFilterModal}
            onClose={() => setShowRoleFilterModal(false)}
            onApply={setSelectedRoles}
            selectedRoles={selectedRoles}
            roles={roles}
          />

          <TimezoneFilterModal
            isOpen={showTimezoneFilterModal}
            onClose={() => setShowTimezoneFilterModal(false)}
            onApply={setSelectedTimezones}
            selectedTimezones={selectedTimezones}
          />

          {/* All Filters Modal */}
          <AgentsFilterModal
            isOpen={showAllFiltersModal}
            onClose={() => setShowAllFiltersModal(false)}
            onApply={handleApplyAllFilters}
            filters={{
              statuses: selectedStatuses,
              departments: selectedDepartments,
              roles: selectedRoles,
              timezones: selectedTimezones
            }}
            options={{
              statusOptions: agentStatusOptions.map(opt => ({
                value: opt.value,
                label: opt.label
              })),
              departments: departments,
              roles: roles,
              timezones: [
                { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-05:00' },
                { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-06:00' },
                { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-07:00' },
                { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-08:00' },
                { value: 'America/Phoenix', label: 'Arizona Time (MST)', offset: '-07:00' },
                { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)', offset: '+00:00' },
                { value: 'Europe/Paris', label: 'Central European Time (CET)', offset: '+01:00' },
                { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)', offset: '+04:00' },
                { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', offset: '+05:30' },
                { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)', offset: '+09:00' },
                { value: 'Asia/Shanghai', label: 'China Standard Time (CST)', offset: '+08:00' },
                { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)', offset: '+10:00' },
              ]
            }}
          />
        </div>
      );
    };

    export default AgentsManagement;